import { ReactElement, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import {
    Button,
    PasswordField,
    StepControl,
    TextField,
} from '@speeki/global-ui-components';

import { StandardForm } from '@components/atoms/StandardForm/StandardForm';

import { TenantMfaSettingsDocument } from '@graphql/generated/graphql';

import { useQueryParam } from '@utils/hooks/useQueryParam';
import { useResponsive } from '@utils/hooks/useResponsive';

import { AuthService } from '@services/AuthService/AuthService';
import { getPath } from '@services/RouteService/paths';

import { useQuery } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { emailValidation } from '@shared/validations';

import { StepControlWrapperElement } from './LoginForm.styled';

interface LoginFormProps {
    bottomElement?: ReactElement;
    handleLoginSubmit: (data: LoginFormValues) => Promise<void>;
    loading: boolean;
    showMfaStep?: boolean;
}

export const LoginForm = ({
    bottomElement,
    handleLoginSubmit,
    loading,
    showMfaStep = true,
}: LoginFormProps) => {
    const navigate = useNavigate();
    const tenantUuid = AuthService.getTenantUuid();
    const { t } = useTranslation();

    const userEmail = useQueryParam('userEmail');

    const {
        formState: { errors, isSubmitted, isSubmitting, isValid },
        handleSubmit,
        register,
        setValue,
    } = useForm<LoginFormValues>({
        mode: 'onChange',
        resolver: yupResolver(LoginFormSchema),
    });

    const isSm = useResponsive('sm');

    const { data } = useQuery(TenantMfaSettingsDocument, {
        skip: !tenantUuid,
        variables: {
            uuid: tenantUuid || '',
        },
    });
    const showMfaLogin = data?.tenant.enforceMfa && showMfaStep;

    const isError = !!Object.keys(errors).length;

    useEffect(() => {
        if (userEmail) setValue('email', userEmail);
    }, [userEmail]);

    return (
        <StandardForm onSubmit={handleSubmit(handleLoginSubmit)}>
            <TextField
                {...register('email')}
                className="htmlToImageIgnore"
                defaultValue={userEmail ?? ''}
                disabled={!!userEmail}
                error={!!errors.email}
                label={t('EMAIL')}
                message={
                    !!errors.email?.message
                        ? t(errors.email?.message)
                        : undefined
                }
                placeholder={t('NEW_LOGIN_EMAIL_PLACEHOLDER')}
                testId="3LO1fJ0"
                type={'text'}
            />
            <PasswordField
                {...register('password')}
                className="htmlToImageIgnore"
                clickableLabel={{
                    onClick: () =>
                        navigate(getPath({ path: '/auth/forgot-password' })),
                    text: t('FORGOT_PASSWORD'),
                }}
                error={!!errors.password}
                label={t('PASSWORD')}
                message={
                    !!errors.password?.message
                        ? t(errors.password?.message)
                        : undefined
                }
                placeholder={t('PASSWORD_PLACEHOLDER')}
                testId="ksL3c1J"
            />
            <StandardForm.Actions>
                <Button
                    disabled={
                        loading ||
                        isSubmitting ||
                        (!isValid && isSubmitted) ||
                        isError
                    }
                    fullWidth
                    isLoading={loading || isSubmitting}
                    testId="button_proceed-next-step"
                    type={'submit'}
                    variant={'primary'}
                >
                    {showMfaLogin ? t('PROCEED_TO_THE_NEXT_STEP') : t('LOGIN')}
                </Button>
                {bottomElement && bottomElement}
                {!isSm && showMfaLogin && (
                    <StepControlWrapperElement>
                        <StepControl
                            activeStep={1}
                            length={2}
                            testId="O08272d"
                            variant="dark"
                        />
                    </StepControlWrapperElement>
                )}
            </StandardForm.Actions>
        </StandardForm>
    );
};

const LoginFormSchema = yup.object({
    email: emailValidation,
    password: yup.string().required('FIELD_CANNOT_BE_EMPTY'),
});

export type LoginFormValues = yup.InferType<typeof LoginFormSchema>;
